import {
  getTableWidgetDataFromApi,
  TableWidgetDataFromApi,
} from '@/models/Table';
import {
  ReportWidgetData,
  TableWidgetData,
  WidgetType,
} from '@/models/PanelWidgets';
import { UpdateReportWidgetResponseBody } from '@/api/ApiResponse';

import { v4 as uuidV4 } from 'uuid';

export type UpdatedReportResponse = {
  reportId: string;
  eventId: string;
};

export type SentenceOffsetFromApi = {
  start: number;
  end: number;
  bounding_boxes: Record<string, number>[];
};

export type SentenceOffset = {
  start: number;
  end: number;
  boundingBoxes: Record<string, number>[];
};

export type SnippetMetadataFromApi = {
  quote: string;
  pdf_hash?: string;
  section_title?: string;
  is_body?: boolean;
  sentence_offsets?: SentenceOffsetFromApi[];
  ref_mentions?: number[];
};

export type SnippetMetadata = {
  quote: string;
  pdfHash?: string;
  sectionTitle?: string;
  isBody?: boolean;
  sentenceOffsets?: SentenceOffset[];
  refMentions?: number[];
};

export type ReportCitationFromApi = {
  id: string;
  corpus_id: number;
  snippets: string[];
  n_citations: number;
  paper?: PaperDetailsFromApi;
  snippet_metadata?: SnippetMetadataFromApi[];
};

export type ReportCitation = {
  id: string;
  corpusId: number;
  snippets: string[];
  paper?: PaperDetails;
  table?: TableWidgetData;
  snippetMetadata?: SnippetMetadata[];
};

export interface PaperDetailsFromApi {
  corpus_id: number;
  title: string;
  year: number;
  venue: string | null;
  authors: Author[];
  n_citations?: number | null;
  [k: string]: unknown;
}

export interface PaperDetails {
  corpusId: number;
  title: string;
  year: number;
  venue: string | null;
  authors: Author[];
  nCitations?: number | null;
}

export interface Author {
  name: string;
  authorId: string | null;
  [k: string]: unknown;
}

export enum ReportSectionFormat {
  LIST = 'list',
  SYNTHESIS = 'synthesis',
}
export interface ReportSection {
  id: string;
  title: string;
  tldr?: string;
  text: string;
  citations?: ReportCitation[];
  format?: ReportSectionFormat;
  table?: TableWidgetData;
}

// keep these models in sync with the nora-widget-service report.py models
export type ReportSectionFromApi = {
  id: string;
  title: string;
  tldr?: string;
  text: string;
  citations?: ReportCitationFromApi[];
  format?: ReportSectionFormat;
  table?: TableWidgetDataFromApi;
};
export type ReportWidgetDataFromApi = {
  id: string;
  sections: ReportSectionFromApi[];
  query?: string;
};
/// /// ///

export const reportPaperDetailsFactory = (
  apiPaper: PaperDetailsFromApi,
): PaperDetails => {
  return {
    corpusId: apiPaper.corpus_id,
    title: apiPaper.title,
    year: apiPaper.year,
    venue: apiPaper.venue,
    authors: apiPaper.authors,
    nCitations: apiPaper.n_citations ?? null,
  };
};

export const reportSectionFactory = (
  apiSection: ReportSectionFromApi,
): ReportSection => {
  return {
    id: apiSection.id,
    title: apiSection.title,
    tldr: apiSection.tldr,
    text: apiSection.text,
    citations: apiSection.citations?.map(reportCitationFactory) ?? [],
    format: apiSection.format ?? undefined,
    table: apiSection.table
      ? getTableWidgetDataFromApi(apiSection.table)
      : undefined,
  };
};

export const reportCitationFactory = (
  apiCitation: ReportCitationFromApi,
): ReportCitation => {
  return {
    id: apiCitation.id,
    corpusId: apiCitation.corpus_id,
    snippets: apiCitation.snippets,
    paper: apiCitation.paper
      ? reportPaperDetailsFactory(apiCitation.paper)
      : undefined,
    snippetMetadata: apiCitation.snippet_metadata?.map(snippetMetadataFactory),
  };
};

export const snippetMetadataFactory = (
  snippetMetadata: SnippetMetadataFromApi,
): SnippetMetadata => {
  return {
    quote: snippetMetadata.quote,
    pdfHash: snippetMetadata.pdf_hash,
    sectionTitle: snippetMetadata.section_title,
    isBody: snippetMetadata.is_body,
    sentenceOffsets: snippetMetadata.sentence_offsets?.map(
      sentenceOffsetFactory,
    ),
    refMentions: snippetMetadata.ref_mentions,
  };
};

export const sentenceOffsetFactory = (
  sentenceOffset: SentenceOffsetFromApi,
): SentenceOffset => {
  return {
    start: sentenceOffset.start,
    end: sentenceOffset.end,
    boundingBoxes: sentenceOffset.bounding_boxes,
  };
};

export const reportWidgetFactory = ({
  id,
  sections,
  query,
}: {
  id?: string;
  sections?: ReportSectionFromApi[];
  query?: string;
} = {}): ReportWidgetData => {
  return {
    id: id ?? uuidV4(),
    type: WidgetType.REPORT,
    sections: sections?.map(reportSectionFactory) ?? [],
    query: query ?? undefined,
  };
};

export function getReportWidgetDataFromApi(
  args: ReportWidgetDataFromApi,
): ReportWidgetData {
  return reportWidgetFactory(args);
}

export function getUpdatedReportResponseFromApi(
  args: UpdateReportWidgetResponseBody,
): UpdatedReportResponse {
  return {
    reportId: args.report_id,
    eventId: args.event_id,
  };
}
